/*
 * <complete-the-set>
 * Builds a weighted recommendation grid from three card pools:
 *   - bestseller : same-category best sellers (rendered server-side)
 *   - new        : best-selling new arrivals  (rendered server-side)
 *   - bought     : bought-together items      (fetched from recommendations API)
 * Fills each source up to its quota (% of grid), dedupes across all sources,
 * excludes the current product, and back-fills short sources so the grid is full.
 *
 * Randomize mode (optional): picks randomly from each source's top pool for
 * variety, and remembers the picks per product in sessionStorage for a TTL so
 * returning to the same product within the window shows the same set.
 */
(function () {
  if (customElements.get('complete-the-set')) return;

  class CompleteTheSet extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    try {

    this.limit = parseInt(this.dataset.limit, 10) || 4;
    this.currentId = this.dataset.currentId || '';
    this.randomize = this.dataset.randomize === 'true';
    this.ttl = parseInt(this.dataset.ttl, 10) || 0;
    this.storeKey = `cts:${this.dataset.sectionId}:${this.currentId}`;
    this.weights = {
      bestseller: parseFloat(this.dataset.pctBestseller) || 0,
      bought: parseFloat(this.dataset.pctBought) || 0,
      new: parseFloat(this.dataset.pctNew) || 0,
    };

    this.grid = this.querySelector('.cts__grid');
    this.boughtLoaded = false;
    this.pools = {
      bestseller: this.readPool('bestseller'),
      new: this.readPool('new'),
      bought: [],
    };

    if (this.grid) this.render(); // fast first paint (deterministic until bought loads)

    const url = this.dataset.url;
    if (url && this.grid) this.fetchBought(url);
    } catch (e) {
      console.error('[complete-the-set]', e);
      this.style.display = 'none';
    }
  }

  readPool(source) {
    const ul = this.querySelector(`.cts-pool[data-source="${source}"]`);
    return ul ? Array.from(ul.querySelectorAll('li.cts-card')) : [];
  }

  fetchBought(url) {
    fetch(url)
      .then((res) => res.text())
      .then((text) => {
        const doc = new DOMParser().parseFromString(text, 'text/html');
        const ul = doc.querySelector('.cts-pool[data-source="bought"]');
        if (ul) {
          this.pools.bought = Array.from(ul.querySelectorAll('li.cts-card')).map((n) =>
            document.importNode(n, true)
          );
        }
      })
      .catch((e) => console.warn('[complete-the-set] recommendations fetch failed', e))
      .finally(() => {
        this.boughtLoaded = true;
        this.render();
      });
  }

  // ---- selection ----------------------------------------------------------

  // Quota per source: round(limit * weight / totalWeight); remainder to bestseller.
  quotas() {
    const order = ['bestseller', 'bought', 'new'];
    const total = order.reduce((s, k) => s + this.weights[k], 0) || 1;
    const q = {};
    let assigned = 0;
    order.forEach((k) => {
      q[k] = Math.round((this.limit * this.weights[k]) / total);
      assigned += q[k];
    });
    let diff = this.limit - assigned;
    while (diff !== 0) {
      if (diff > 0) {
        q.bestseller += 1;
        diff -= 1;
      } else {
        const k = q.new > 0 ? 'new' : q.bought > 0 ? 'bought' : 'bestseller';
        q[k] = Math.max(0, q[k] - 1);
        diff += 1;
      }
    }
    return q;
  }

  // Pick nodes by quota from the given pools (pass 1: quotas, pass 2: back-fill).
  pickByQuota(pools) {
    const order = ['bestseller', 'bought', 'new'];
    const q = this.quotas();
    const used = new Set();
    if (this.currentId) used.add(this.currentId);
    const chosen = [];

    order.forEach((source) => {
      let need = q[source];
      for (const node of pools[source]) {
        if (need <= 0) break;
        const id = node.dataset.productId;
        if (used.has(id)) continue;
        used.add(id);
        chosen.push(node);
        need -= 1;
      }
    });

    if (chosen.length < this.limit) {
      for (const source of order) {
        for (const node of pools[source]) {
          if (chosen.length >= this.limit) break;
          const id = node.dataset.productId;
          if (used.has(id)) continue;
          used.add(id);
          chosen.push(node);
        }
      }
    }
    return chosen.slice(0, this.limit);
  }

  render() {
    let chosen;

    if (this.randomize && this.boughtLoaded) {
      const stored = this.getStored();
      if (stored) {
        chosen = this.nodesFromIds(stored);
        if (chosen.length < this.limit) chosen = this.backfill(chosen); // OOS / removed items
      } else {
        const shuffled = {
          bestseller: this.shuffle(this.pools.bestseller.slice()),
          bought: this.shuffle(this.pools.bought.slice()),
          new: this.shuffle(this.pools.new.slice()),
        };
        chosen = this.pickByQuota(shuffled);
        this.setStored(chosen.map((n) => n.dataset.productId));
      }
    } else {
      chosen = this.pickByQuota(this.pools);
    }

    if (chosen.length) this.grid.replaceChildren(...chosen);
  }

  // ---- helpers ------------------------------------------------------------

  allNodes() {
    return [...this.pools.bestseller, ...this.pools.bought, ...this.pools.new];
  }

  nodesFromIds(ids) {
    const byId = new Map();
    this.allNodes().forEach((n) => {
      if (!byId.has(n.dataset.productId)) byId.set(n.dataset.productId, n);
    });
    const out = [];
    const used = new Set();
    ids.forEach((id) => {
      if (byId.has(id) && !used.has(id)) {
        used.add(id);
        out.push(byId.get(id));
      }
    });
    return out;
  }

  backfill(chosen) {
    const out = chosen.slice();
    const used = new Set(out.map((n) => n.dataset.productId));
    if (this.currentId) used.add(this.currentId);
    for (const node of this.allNodes()) {
      if (out.length >= this.limit) break;
      const id = node.dataset.productId;
      if (used.has(id)) continue;
      used.add(id);
      out.push(node);
    }
    return out.slice(0, this.limit);
  }

  shuffle(arr) {
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  }

  getStored() {
    if (!this.ttl) return null;
    try {
      const raw = window.sessionStorage.getItem(this.storeKey);
      if (!raw) return null;
      const data = JSON.parse(raw);
      if (!data || !Array.isArray(data.ids)) return null;
      if (Date.now() - data.ts > this.ttl) {
        window.sessionStorage.removeItem(this.storeKey);
        return null;
      }
      return data.ids;
    } catch (e) {
      return null;
    }
  }

  setStored(ids) {
    if (!this.ttl) return;
    try {
      window.sessionStorage.setItem(this.storeKey, JSON.stringify({ ts: Date.now(), ids }));
    } catch (e) {
      /* storage unavailable — ignore */
    }
  }
}

  customElements.define('complete-the-set', CompleteTheSet);
})();
